import { Box, Stack, Typography } from '@mui/material';
import { GraphicUploader } from 'components/common/graphicUploader';
import { useGeneralNormDocumentRouteSync } from 'components/common/graphicUploader/documentRouteSync';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import {
  useInspection,
  useInspectionTemplate,
  useInspectionTemplateOnView,
} from 'store/inspection';
import { RichTextView } from 'components/common/RichTextEditor/RichTextView';
import {
  DocumentToTemplateBinder,
  IDocumentBinder,
} from 'shared/domain/document/documentBinder';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { mergeSx } from 'merge-sx';
import { createInspectionTemplateGeneralNormDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import {
  GeneralNormPreviewProps,
  GeneralNormPreviewWithGraphicUploaderProps,
} from './types';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { createInspectionGeneralNormRoute } from 'views/inspections/single/helpers';

import { containerSx, uploaderWrapperSx, contentSx } from './styles';
import { formatTranslation } from 'intl';
import { InspectionTemplateTranslationKeys } from 'intl/keys';
import { hasGeneralNormContent } from './model';

function GeneralNormPreview({
  generalNormDescription,
  GU,
  sx,
}: GeneralNormPreviewProps): ReactElement {
  const intl = useIntl();

  return (
    <Box sx={mergeSx(containerSx, sx?.container)}>
      <Stack spacing={2} sx={mergeSx(contentSx, sx?.content)}>
        {generalNormDescription ? (
          <Box>
            <Typography variant='caption' color='text.secondary'>
              {formatTranslation(
                intl,
                InspectionTemplateTranslationKeys.fieldLabelStandardDescription
              )}
            </Typography>
            <RichTextView content={generalNormDescription} />
          </Box>
        ) : null}
        <Box sx={mergeSx(uploaderWrapperSx, sx?.uploaderWrapper)}>
          <GraphicUploader {...GU} />
        </Box>
      </Stack>
    </Box>
  );
}

function ContextedGeneralNormPreview({
  sx,
}: Pick<GeneralNormPreviewProps, 'sx'>): ReactElement | null {
  const { template } = useInspectionTemplateOnView();

  if (!template) return null;

  return (
    <GeneralNormPreviewWithGraphicUploader template={template} sx={sx} />
  );
}

function GeneralNormPreviewWithGraphicUploader({
  template,
  sx,
}: GeneralNormPreviewWithGraphicUploaderProps): ReactElement | null {
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const { inspection } = useInspection();
  const { inspectionTemplateDocuments } = useInspectionTemplate();
  const generalNormDescription = template.generalNorm.description;
  const inspectionId = inspection?._id;

  const getInspectionBaseUrl = useCallback(
    () =>
      createInspectionGeneralNormRoute(
        projectId,
        inspectionId ?? '',
        template._id
      ),
    [inspectionId, projectId, template._id]
  );

  const routeSync = useGeneralNormDocumentRouteSync(
    inspectionId,
    getInspectionBaseUrl,
    template._id
  );

  const documentScopeKey = useMemo(
    () =>
      getDocumentScopeKey({
        ...(inspectionId ? { inspectionId } : {}),
        templateId: template._id,
      }),
    [inspectionId, template._id]
  );

  const uploaderConfig = useMemo(
    () => ({
      baseUrl: createInspectionTemplateGeneralNormDocumentsBaseUrl(
        template._id
      ),
      documentScopeKey,
      previewOnly: true as const,
      internalEntityBaseUrl: routeSync.internalEntityBaseUrl,
      autoOpenCheck: routeSync.autoOpenCheck,
      documentBinderFactory: (): IDocumentBinder =>
        new DocumentToTemplateBinder({
          templateId: template._id,
          ...(inspectionId ? { inspectionId } : {}),
        }),
    }),
    [
      documentScopeKey,
      inspectionId,
      routeSync.autoOpenCheck,
      routeSync.internalEntityBaseUrl,
      template._id,
    ]
  );

  const GU = useGraphicUploader(
    inspectionTemplateDocuments.templateDocuments,
    uploaderConfig,
    true
  );

  if (!hasGeneralNormContent(template, inspectionTemplateDocuments)) {
    return null;
  }

  return (
    <GeneralNormPreview
      sx={sx}
      generalNormDescription={generalNormDescription}
      GU={GU}
    />
  );
}

export { GeneralNormPreview, ContextedGeneralNormPreview };
